/**
 * Canonical deal funnel — the single source of truth for how the two
 * persisted stage fields on a project relate:
 *
 *   - `project.stage` (process funnel): screening → diligence → ic → closed,
 *     with `passed` as the terminal disposition off the path. This is THE
 *     canonical field: it is what backend stage-scoping reads to size
 *     IC memos / committees / DDQs.
 *   - `project.dealLifecycle` (relationship status): prospect → active_deal →
 *     portfolio_company → exited, with `dead` terminal. A DERIVED ROLLUP of
 *     the funnel — kept consistent by routing every stage-ish write through
 *     `applyStageChange` / `applyLifecycleChange`.
 *
 * Leaf module (no firebase imports) so it stays unit-testable. The
 * DealLifecycle union mirrors hooks/useProjects.ts; TypeScript's structural
 * typing keeps them interchangeable.
 */

export type CanonicalDealStage = 'screening' | 'diligence' | 'ic' | 'closed' | 'passed';
export type DealLifecycle = 'prospect' | 'active_deal' | 'portfolio_company' | 'exited' | 'dead';

/** Forward path through the funnel. `passed` is a disposition, not a step. */
export const DEAL_STAGE_ORDER: CanonicalDealStage[] = ['screening', 'diligence', 'ic', 'closed'];

export const DEAL_STAGE_LABELS: Record<CanonicalDealStage, string> = {
  screening: 'Screening',
  diligence: 'Due Diligence',
  ic: 'IC Review',
  closed: 'Closed',
  passed: 'Passed',
};

/** Forward order of the lifecycle pipeline. `dead` is terminal, off-path. */
const LIFECYCLE_RANK: Record<Exclude<DealLifecycle, 'dead'>, number> = {
  prospect: 0,
  active_deal: 1,
  portfolio_company: 2,
  exited: 3,
};

/** What the lifecycle SHOULD read for a deal at the given process stage. */
export function deriveDealLifecycle(stage: CanonicalDealStage): DealLifecycle {
  switch (stage) {
    case 'screening':
      return 'prospect';
    case 'diligence':
    case 'ic':
      return 'active_deal';
    case 'closed':
      return 'portfolio_company';
    case 'passed':
      return 'dead';
  }
}

/** The earliest process stage consistent with a lifecycle status. */
export function minStageForLifecycle(lifecycle: DealLifecycle): CanonicalDealStage {
  switch (lifecycle) {
    case 'prospect':
      return 'screening';
    case 'active_deal':
      return 'diligence';
    case 'portfolio_company':
    case 'exited':
      return 'closed';
    case 'dead':
      return 'passed';
  }
}

function stageRank(stage: CanonicalDealStage): number {
  const index = DEAL_STAGE_ORDER.indexOf(stage);
  // `passed` (and any unknown) ranks below the path so ratchets can move
  // a revived deal back onto it.
  return index === -1 ? -1 : index;
}

/**
 * Resolve the consistent (stage, lifecycle) pair after a STAGE change.
 *
 * Lifecycle is a rollup: it follows the stage forward but never silently
 * downgrades — except a deal marked `passed` always rolls to `dead`, and a
 * `dead` deal whose stage moves back onto the path is revived. A manual
 * `exited` (carries exit metadata) is never overwritten by derivation.
 */
export function applyStageChange(
  newStage: CanonicalDealStage,
  currentLifecycle: DealLifecycle | null | undefined,
): { stage: CanonicalDealStage; dealLifecycle: DealLifecycle } {
  if (newStage === 'passed') {
    // Exited deals keep their exit record even if someone marks the stage passed.
    return { stage: newStage, dealLifecycle: currentLifecycle === 'exited' ? 'exited' : 'dead' };
  }

  const derived = deriveDealLifecycle(newStage);
  if (currentLifecycle == null || currentLifecycle === 'dead') {
    // Unset → adopt the rollup. Dead + back on the path → revival.
    return { stage: newStage, dealLifecycle: derived };
  }
  if (currentLifecycle === 'exited') {
    return { stage: newStage, dealLifecycle: 'exited' };
  }

  const keepCurrent =
    LIFECYCLE_RANK[currentLifecycle] >= LIFECYCLE_RANK[derived as Exclude<DealLifecycle, 'dead'>];
  return { stage: newStage, dealLifecycle: keepCurrent ? currentLifecycle : derived };
}

/**
 * Resolve the consistent (stage, lifecycle) pair after a LIFECYCLE change
 * (the header toggle). The stage ratchets FORWARD to stay consistent —
 * advancing the lifecycle never leaves the funnel behind (which would
 * silently under-scope backend generation) — but a backward lifecycle move
 * never downgrades the stage.
 */
export function applyLifecycleChange(
  newLifecycle: DealLifecycle,
  currentStage: CanonicalDealStage | null | undefined,
): { stage: CanonicalDealStage; dealLifecycle: DealLifecycle } {
  if (newLifecycle === 'dead') {
    return { stage: 'passed', dealLifecycle: 'dead' };
  }

  const minStage = minStageForLifecycle(newLifecycle);
  const current = currentStage ?? 'screening';
  const stage = stageRank(current) >= stageRank(minStage) ? current : minStage;
  return { stage, dealLifecycle: newLifecycle };
}

/**
 * Pipeline counts for funnel summaries. Uses funnel semantics: a `dead`
 * lifecycle counts as `passed` regardless of stage (legacy docs may disagree),
 * and unset/unknown stages count as `screening` (the funnel's entry point,
 * matching the `?? 'screening'` default in applyLifecycleChange).
 */
export function countDealsByStage(
  deals: ReadonlyArray<{ stage?: string | null; dealLifecycle?: string | null }>,
): Record<CanonicalDealStage, number> {
  const counts: Record<CanonicalDealStage, number> = {
    screening: 0,
    diligence: 0,
    ic: 0,
    closed: 0,
    passed: 0,
  };
  for (const deal of deals) {
    if (deal.dealLifecycle === 'dead' || deal.stage === 'passed') {
      counts.passed += 1;
    } else if (deal.stage && DEAL_STAGE_ORDER.includes(deal.stage as CanonicalDealStage)) {
      counts[deal.stage as CanonicalDealStage] += 1;
    } else {
      counts.screening += 1;
    }
  }
  return counts;
}

/** A deal that is out of the active pipeline (no progress UI should imply otherwise). */
export function isTerminalDeal(
  stage: CanonicalDealStage | null | undefined,
  lifecycle: DealLifecycle | null | undefined,
): boolean {
  return stage === 'passed' || lifecycle === 'dead' || lifecycle === 'exited';
}

/** Human label for the terminal state, for the disposition chip. */
export function terminalDealLabel(
  stage: CanonicalDealStage | null | undefined,
  lifecycle: DealLifecycle | null | undefined,
): string | null {
  if (lifecycle === 'exited') return 'Exited';
  if (lifecycle === 'dead' || stage === 'passed') return 'Passed';
  return null;
}
